/**
 * Library browsing: ordering and relatedness. Both pure, both here rather than in the screen,
 * because the API needs relatedness too and a second implementation would drift.
 */
import type { Method, KitLocale } from "./types.js";
import { parseGroupSize } from "./filter.js";

export type SortOrder = "title" | "duration-asc" | "duration-desc" | "group-asc" | "group-desc";

export const SORT_ORDERS: SortOrder[] = ["title", "duration-asc", "duration-desc", "group-asc", "group-desc"];

/**
 * `durationMin` and `groupSize` are both nullable, and a method with no duration is not a
 * zero-minute method — it is unknown. Unknowns sort LAST in every order rather than clumping at
 * the top of ascending and the bottom of descending, which would make the same method look like
 * both the shortest and the longest depending on which button you pressed.
 */
function nullsLast(a: number | null, b: number | null, dir: 1 | -1): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return (a - b) * dir;
}

function groupFloor(m: Method): number | null {
  if (!m.groupSize) return null;
  const { min } = parseGroupSize(m.groupSize);
  // parseGroupSize opens the filter wide on unparseable input by returning {0, Infinity}; for
  // SORTING that same 0 would rank an unparseable value as the smallest group, so treat it as
  // unknown instead.
  return min === 0 ? null : min;
}

export function sortMethods(methods: readonly Method[], order: SortOrder, locale: KitLocale = "pt-BR"): Method[] {
  const byTitle = (a: Method, b: Method): number => a.title.localeCompare(b.title, locale);
  const out = [...methods];
  switch (order) {
    case "duration-asc":
      return out.sort((a, b) => nullsLast(a.durationMin, b.durationMin, 1) || byTitle(a, b));
    case "duration-desc":
      return out.sort((a, b) => nullsLast(a.durationMin, b.durationMin, -1) || byTitle(a, b));
    case "group-asc":
      return out.sort((a, b) => nullsLast(groupFloor(a), groupFloor(b), 1) || byTitle(a, b));
    case "group-desc":
      return out.sort((a, b) => nullsLast(groupFloor(a), groupFloor(b), -1) || byTitle(a, b));
    default:
      return out.sort(byTitle);
  }
}

/**
 * The subset relatedness actually reads. Declared narrowly so the API can answer from a slim
 * `SELECT` instead of pulling every `bodyMd` in the library on the most-crawled route. `Method`
 * satisfies it structurally, so callers holding full methods pass them unchanged.
 */
export type RelatableMethod = Pick<Method, "slug" | "title" | "tags" | "mode" | "durationMin" | "locale">;

export interface RelatedMethod {
  slug: string;
  title: string;
  mode: Method["mode"];
  durationMin: number | null;
  /** How many tags it shares with the target — exposed so a caller can hide weak matches. */
  shared: number;
}

/**
 * Methods related to `target`, by tag overlap.
 *
 * Ranked by shared-tag count, then by same-mode (a presencial method is a poor suggestion on an
 * online one), then title for determinism. Candidates sharing NO tag are dropped entirely — an
 * unrelated "related" list is worse than an empty one, because it teaches the reader to ignore
 * the section.
 *
 * Other LOCALE VARIANTS OF THE TARGET ARE EXCLUDED: they share the slug, so suggesting them would
 * link the page to itself. Same for other variants of the same suggestion — the list is deduped
 * by slug, keeping the first (best-ranked) variant.
 */
export function relatedMethods(all: readonly RelatableMethod[], target: RelatableMethod, limit = 4): RelatedMethod[] {
  const wanted = new Set(target.tags);
  if (wanted.size === 0) return [];

  const scored = all
    .filter((m) => m.slug !== target.slug)
    .map((m) => ({ m, shared: m.tags.reduce((n, tag) => n + (wanted.has(tag) ? 1 : 0), 0) }))
    .filter((x) => x.shared > 0)
    .sort(
      (a, b) =>
        b.shared - a.shared ||
        Number(b.m.mode === target.mode) - Number(a.m.mode === target.mode) ||
        a.m.title.localeCompare(b.m.title, target.locale),
    );

  const seen = new Set<string>();
  const out: RelatedMethod[] = [];
  for (const { m, shared } of scored) {
    if (seen.has(m.slug)) continue;
    seen.add(m.slug);
    out.push({ slug: m.slug, title: m.title, mode: m.mode, durationMin: m.durationMin, shared });
    if (out.length === limit) break;
  }
  return out;
}
